import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  cancelReservationCheckout,
  getReservationCheckout,
} from '../api/bookings';
import { HomeHeader } from '../components/home/HomeHeader';
import { ErrorIcon } from '../components/shared/Icons';
import { loadAuthSession } from '../data/authSession';
import {
  clearPendingCheckoutContext,
  getPendingCheckoutContext,
} from '../data/pendingCheckoutStorage';
import {
  paymentFailureFromSearchParams,
  roomDetailPathFromPaymentContext,
} from '../utils/paymentFailure';
import '../styles/payment-result.css';

function checkoutStepKey(checkoutId: string, step: string) {
  return `web:${checkoutId}:${step}`;
}

export function PaymentFailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authSession = loadAuthSession();
  const isAuthenticated = Boolean(authSession);

  const failure = paymentFailureFromSearchParams(searchParams);
  const roomDetailPath = roomDetailPathFromPaymentContext(searchParams);

  // 보존된 checkout 컨텍스트 — SpaceReservationPage 가 토스 SDK 호출 직전에
  // 남겨둔 것. PR-A 정책: 자동 cancel 호출 없이 사용자 명시 선택만 처리.
  // Fix 3: 공용 getPendingCheckoutContext 사용.
  const pendingContext = useMemo(() => getPendingCheckoutContext(), []);

  const [isAbandoning, setIsAbandoning] = useState(false);

  const handleRetry = useCallback(() => {
    // Fix 1: resumeCheckoutId query param 으로 SpaceReservationPage 에 진입.
    // 페이지가 마운트되면 handleResumePayment 가 자동으로 토스 결제창을 재호출.
    // 새 booking 생성 없이 보존된 PENDING_PAYMENT checkout 을 그대로 사용.
    if (pendingContext.checkoutId) {
      const roomSlug = pendingContext.roomSlug;
      const params = new URLSearchParams();
      params.set('resumeCheckoutId', pendingContext.checkoutId);
      if (pendingContext.roomId) params.set('roomId', pendingContext.roomId);
      // searchParams 에서 date 를 전달해서 availability 조회도 맞춤.
      const date = searchParams.get('date');
      if (date) params.set('date', date);
      const path = roomSlug
        ? `/spaces/${roomSlug}/reserve?${params.toString()}`
        : `/`;
      navigate(path);
      return;
    }
    // checkoutId 없음 — room 상세로 복귀.
    navigate(roomDetailPath);
  }, [pendingContext, navigate, roomDetailPath, searchParams]);

  const [abandonError, setAbandonError] = useState<string | null>(null);

  const handleAbandon = useCallback(() => {
    if (isAbandoning) {
      return;
    }
    setIsAbandoning(true);
    setAbandonError(null);
    const checkoutId = pendingContext.checkoutId;
    const storedRevision = Number(pendingContext.checkoutRevision);

    const cancelPromise = (async () => {
      if (checkoutId) {
        const revision = Number.isSafeInteger(storedRevision) && storedRevision > 0
          ? storedRevision
          : (await getReservationCheckout(checkoutId)).revision;
        await cancelReservationCheckout(
          checkoutId,
          revision,
          checkoutStepKey(checkoutId, `abandon:${failure.code ?? 'USER_CANCEL'}`),
        );
      }
    })();

    // Fix 4: cancel 실패 시 사용자에게 에러 안내 + 페이지에 머무름.
    // 성공 시에만 sessionStorage 정리 후 마이페이지로 이동.
    void cancelPromise
      .then(() => {
        clearPendingCheckoutContext();
        navigate('/my-reservations');
      })
      .catch(() => {
        setAbandonError('예약 취소에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => {
        setIsAbandoning(false);
      });
  }, [failure.code, failure.message, isAbandoning, navigate, pendingContext]);

  return (
    <main className="payment-result">
      <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />
      <section className="payment-result__shell">
        <span aria-hidden="true" className="payment-result__icon payment-result__icon--error">
          <ErrorIcon />
        </span>
        <h2 className="payment-result__title">결제가 완료되지 않았습니다</h2>
        <p className="payment-result__description">{failure.message}</p>
        {failure.code ? (
          <p className="payment-result__error-code">오류 코드: {failure.code}</p>
        ) : null}
        <p className="payment-result__description">
          예약은 결제 대기 상태로 유지됩니다. 다시 결제하거나 예약을 포기할 수 있어요.
        </p>
        {abandonError ? (
          <p className="payment-result__error-code" role="alert" data-testid="abandon-error">
            {abandonError}
          </p>
        ) : null}
        <div className="payment-result__actions">
          <button
            className="payment-result__button payment-result__button--secondary"
            disabled={isAbandoning}
            onClick={handleAbandon}
            type="button"
            data-testid="payment-fail-abandon"
          >
            {isAbandoning ? '예약 취소 중…' : '예약 포기'}
          </button>
          <button
            className="payment-result__button payment-result__button--primary"
            disabled={isAbandoning}
            onClick={handleRetry}
            type="button"
            data-testid="payment-fail-retry"
          >
            다시 결제하기
          </button>
        </div>
      </section>
    </main>
  );
}
